"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { VennDiagram } from "@/components/tool-kit/diagrams/venn-diagram";
import {
  type VennOutside,
  VennOutsideField,
  VennSetPanel,
} from "@/components/tool-kit/diagrams/venn-fields";
import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  layoutVenn,
  REGION_IDS,
  type RegionId,
  type SetCount,
} from "@/lib/tool-kit/diagrams/venn";
import {
  readVennContent,
  regionName,
  SET_LETTERS,
  type VennContent,
  type VennMode,
  vennRows,
  vennWorking,
} from "@/lib/tool-kit/diagrams/venn-input";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const VENN_DIAGRAM_MAKER_STRINGS = {
  setsLabel: "Number of sets",
  twoSets: "Two sets",
  threeSets: "Three sets",
  modeLabel: "What you enter",
  modeItems: "Items in each region",
  modeSets: "Members of each set",
  modeCounts: "Counts",
  modeItemsHint:
    "Type each region's items, one per line. Best for compare-and-contrast notes.",
  modeSetsHint:
    "List each set's members and the tool sorts them into the regions: anything listed under two sets goes in their overlap. Capitals are ignored when matching.",
  modeCountsHint:
    "Type how many are in each region — not in each whole set. Leave a region blank to show nothing in it; it counts as 0 in the working.",
  setName: "Name of set {letter}",
  setColor: "Color of set {letter}",
  exampleNameA: "Mitosis",
  exampleNameB: "Meiosis",
  defaultNameC: "C",
  exampleTitle: "Mitosis and Meiosis Compared",
  exampleItemsA:
    "Two daughter cells\nDaughter cells identical to the parent\nGrowth and repair",
  exampleItemsAB:
    "DNA is copied beforehand\nProphase, metaphase, anaphase, telophase\nHappens in eukaryotic cells",
  exampleItemsB:
    "Four daughter cells\nDaughter cells genetically different\nMakes gametes (sex cells)\nCrossing over in prophase I\nTwo rounds of division",
  regionOnly: "{name} only",
  regionBoth: "Both",
  regionPair: "{first} and {second} only",
  regionAll: "All three",
  regionOutside: "Outside every set",
  itemsLabel: "{region} — one item per line",
  membersLabel: "Members of {name}",
  membersPlaceholderA: "2, 4, 6, 8, 10, 12",
  membersPlaceholderB: "3, 6, 9, 12",
  membersPlaceholderC: "5, 10",
  countLabel: "{region} — count",
  universeToggle: "Draw the universal set: a rectangle around the circles",
  universeName: "Name of the universal set",
  universeDefault: "U",
  outsideItems: "Items outside every set — one per line",
  outsideCount: "Count outside every set",
  universeMembers: "Every member of the universal set",
  universeMembersHint:
    "Members not listed under any set are drawn outside the circles.",
  workingHeading: "Set sizes and the working",
  key: "{letter} = {name}",
  setSize: "n({letter}) = {count}",
  pairSize: "n({first} ∩ {second}) = {count}",
  unionTwo: "n(A ∪ B) = n(A) + n(B) − n(A ∩ B) = {a} + {b} − {ab} = {union}",
  unionThree:
    "n(A ∪ B ∪ C) = n(A) + n(B) + n(C) − n(A ∩ B) − n(A ∩ C) − n(B ∩ C) + n(A ∩ B ∩ C) = {a} + {b} + {c} − {ab} − {ac} − {bc} + {abc} = {union}",
  universeTotal:
    "n(U) = n(inside the circles) + n(outside) = {union} + {outside} = {total}",
  regionColumn: "Region",
  notationColumn: "Set notation",
  countColumn: "Count",
  itemsColumn: "Items",
  more: "+{count} more",
  hiddenItems:
    "one:{count} item did not fit in its region and is shown as “+ more” — the table under the diagram lists everything.|other:{count} items did not fit in their regions and are shown as “+ more” — the table under the diagram lists everything.",
  notInUniverse:
    "one:{items} is in a set but missing from the universal set. Every set lies inside the universal set, so it was counted as a member.|other:{items} are in a set but missing from the universal set. Every set lies inside the universal set, so they were counted as members.",
  negativeCount:
    "one:A count cannot be negative, so {regions} was left blank.|other:Counts cannot be negative, so {regions} were left blank.",
  regionSummary: "{region}: {count}",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Diagram title",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout. The set names stay in the image.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the regions as a table",
  chartLabel: "Venn diagram",
  chartDescription: "Venn diagram of {sets}. {regions}.",
} as const;

type Strings = typeof VENN_DIAGRAM_MAKER_STRINGS;
type Format = (value: number) => string;

const BLANK: Record<RegionId, string> = {
  a: "",
  b: "",
  c: "",
  ab: "",
  ac: "",
  bc: "",
  abc: "",
};

function notices(
  content: VennContent,
  hidden: number,
  nameOf: (id: RegionId) => string,
  strings: Strings,
  locale: string,
  fmt: Format
): string[] {
  const list: string[] = [];
  if (content.negative.length > 0) {
    list.push(
      tPlural(strings.negativeCount, locale, content.negative.length, {
        regions: content.negative.map(nameOf).join(", "),
      })
    );
  }
  if (content.notInUniverse.length > 0) {
    list.push(
      tPlural(strings.notInUniverse, locale, content.notInUniverse.length, {
        items: content.notInUniverse.join(", "),
      })
    );
  }
  if (hidden > 0) {
    list.push(
      tPlural(strings.hiddenItems, locale, hidden, { count: fmt(hidden) })
    );
  }
  return list;
}

function replaceAt<T>(list: readonly T[], index: number, value: T): T[] {
  return list.map((old, i) => (i === index ? value : old));
}

export default function VennDiagramMakerWidget() {
  const strings = useToolUi(VENN_DIAGRAM_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [setCount, setSetCount] = useState<SetCount>(2);
  const [mode, setMode] = useState<VennMode>("items");
  const [names, setNames] = useState<string[]>([
    strings.exampleNameA,
    strings.exampleNameB,
    strings.defaultNameC,
  ]);
  const [colors, setColors] = useState<ChartColorId[]>([
    "blue",
    "orange",
    "green",
  ]);
  const [regionText, setRegionText] = useState<Record<RegionId, string>>({
    ...BLANK,
    a: strings.exampleItemsA,
    ab: strings.exampleItemsAB,
    b: strings.exampleItemsB,
  });
  const [regionCounts, setRegionCounts] = useState(BLANK);
  const [members, setMembers] = useState<string[]>(["", "", ""]);
  const [universe, setUniverse] = useState(false);
  const [universeName, setUniverseName] = useState<string>(
    strings.universeDefault
  );
  const [outside, setOutside] = useState<VennOutside>({
    items: "",
    count: "",
    members: "",
  });
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const ids = REGION_IDS[setCount];
  const shown = names
    .slice(0, setCount)
    .map((name, i) => name.trim() || SET_LETTERS[i]);
  const content = readVennContent(mode, setCount, {
    regionText,
    regionCounts,
    members,
    outside,
    universe,
  });
  const counting = mode === "counts";
  const nameOf = (id: RegionId) => regionName(id, setCount, shown, strings);
  const asText = (count: number | null) => (count === null ? [] : [fmt(count)]);
  const layout = layoutVenn({
    setCount,
    names: shown,
    regions: Object.fromEntries(
      ids.map((id) => [
        id,
        counting ? asText(content.counts[id]) : content.items[id],
      ])
    ),
    counts: counting,
    universe: universe
      ? {
          label: universeName.trim() || strings.universeDefault,
          outside: counting
            ? asText(content.outsideCount)
            : content.outsideItems,
        }
      : null,
    title: style.apa ? "" : style.title,
    more: (count) => t(strings.more, { count: fmt(count) }),
  });
  const hidden = layout.regions.reduce(
    (sum, r) => sum + (r.block?.hidden ?? 0),
    0
  );
  const head = [
    strings.regionColumn,
    strings.notationColumn,
    strings.countColumn,
    ...(counting ? [] : [strings.itemsColumn]),
  ];
  const rows = vennRows(
    setCount,
    content,
    { counting, universe, outsideName: strings.regionOutside },
    nameOf,
    fmt
  );
  const description = t(strings.chartDescription, {
    sets: shown.join(", "),
    regions: ids
      .map((id) =>
        t(strings.regionSummary, {
          region: nameOf(id),
          count: fmt(content.counts[id] ?? 0),
        })
      )
      .join("; "),
  });
  const placeholders = [
    strings.membersPlaceholderA,
    strings.membersPlaceholderB,
    strings.membersPlaceholderC,
  ];
  const modeHints: Record<VennMode, string> = {
    items: strings.modeItemsHint,
    sets: strings.modeSetsHint,
    counts: strings.modeCountsHint,
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.setsLabel}
          name="vdm-sets"
          onChange={(value) => setSetCount(value === "3" ? 3 : 2)}
          options={[
            { value: "2", label: strings.twoSets },
            { value: "3", label: strings.threeSets },
          ]}
          value={String(setCount)}
        />
        <PillGroup
          label={strings.modeLabel}
          name="vdm-mode"
          onChange={setMode}
          options={[
            { value: "items", label: strings.modeItems },
            { value: "sets", label: strings.modeSets },
            { value: "counts", label: strings.modeCounts },
          ]}
          value={mode}
        />
      </div>
      <p className={HINT}>{modeHints[mode]}</p>
      <div
        className={`grid gap-4 sm:grid-cols-2 ${setCount === 3 ? "lg:grid-cols-3" : ""}`}
      >
        {shown.map((name, index) => (
          <VennSetPanel
            color={colors[index]}
            index={index}
            key={SET_LETTERS[index]}
            labels={strings}
            letter={SET_LETTERS[index]}
            members={members[index]}
            mode={mode}
            name={names[index]}
            onColor={(value) =>
              setColors((old) => replaceAt(old, index, value))
            }
            onMembers={(value) =>
              setMembers((old) => replaceAt(old, index, value))
            }
            onName={(value) => setNames((old) => replaceAt(old, index, value))}
            placeholder={placeholders[index]}
            shownName={name}
          />
        ))}
      </div>
      {mode === "sets" ? null : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ids.map((id) =>
            counting ? (
              <TextField
                id={`vdm-count-${id}`}
                key={id}
                label={t(strings.countLabel, { region: nameOf(id) })}
                numeric
                onChange={(value) =>
                  setRegionCounts((old) => ({ ...old, [id]: value }))
                }
                value={regionCounts[id]}
              />
            ) : (
              <TextAreaField
                id={`vdm-items-${id}`}
                key={id}
                label={t(strings.itemsLabel, { region: nameOf(id) })}
                onChange={(value) =>
                  setRegionText((old) => ({ ...old, [id]: value }))
                }
                prose
                value={regionText[id]}
              />
            )
          )}
        </div>
      )}
      <Panel>
        <Checkbox
          checked={universe}
          label={strings.universeToggle}
          onChange={setUniverse}
        />
        {universe ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="vdm-universe-name"
              label={strings.universeName}
              onChange={setUniverseName}
              value={universeName}
            />
            <VennOutsideField
              labels={strings}
              mode={mode}
              onChange={(patch) => setOutside((old) => ({ ...old, ...patch }))}
              outside={outside}
            />
          </div>
        ) : null}
      </Panel>
      <div aria-live="polite" className="space-y-2">
        {notices(content, hidden, nameOf, strings, locale, fmt).map(
          (notice) => (
            <Notice key={notice}>{notice}</Notice>
          )
        )}
      </div>
      <ChartFigure
        apa={style.apa}
        data={toTsv([
          head,
          ...vennRows(
            setCount,
            content,
            { counting, universe, outsideName: strings.regionOutside },
            nameOf,
            String
          ),
        ])}
        figureNumber={style.figureNumber}
        filename={chartFilename(style.title, "venn-diagram")}
        labels={strings}
        note={style.note}
        table={{ head, rows }}
        title={style.title}
      >
        <VennDiagram
          colors={colors
            .slice(0, setCount)
            .map((color) => CHART_COLORS[colorIndex(color)])}
          description={description}
          id="vdm-diagram"
          label={style.title.trim() || strings.chartLabel}
          layout={layout}
        />
      </ChartFigure>
      <div aria-live="polite" className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        {vennWorking(setCount, content, shown, strings, fmt).map((line) => (
          <p className="break-words text-foreground text-sm" key={line}>
            {line}
          </p>
        ))}
      </div>
      <ChartStyleFields
        idPrefix="vdm"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
